import { RiCompass4Line, Stack } from 'component-library';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import { CapsuleTrend } from '@/components';
import { ConservationExplainerWell, NCCardSectionHeader } from '@/components/NCCard/NCCard';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useGetExplainerFallbackText } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetExplainerFallbackText';
import { useGetTrendLabel } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetTrendLabel';
import { FactCapsule } from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/FactCapsule';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { useLatestAnalysisDatestamp } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useLatestAnalysisDatestamp';
import { isNullish } from '@/utils/booleans';

const FACTS = {
  baseline: 'r1_conservation_degradation_drivers_i_baseline',
  latest: 'r1_conservation_degradation_drivers_i',
  change: 'r1_conservation_threats_change_percentage',
} as const;

export const StatsTile = () => {
  const { t } = useTranslation();

  const projectBaseline = useFact<number>(FACTS.baseline);
  const projectLatestValue = useFact<number>(FACTS.latest);
  const changePctFromBaseline = useFact<number>(FACTS.change).value;

  const latestAnalysisDatestamp = useLatestAnalysisDatestamp(
    projectLatestValue?.measured_at ? { facts: projectLatestValue } : undefined,
  );

  const trendLabel = useGetTrendLabel({
    trendChangeFact: FACTS.change,
  });

  const currentYear = useFact<number>('r1_conservation_current_year');
  const baselineYear = useFact<number>('r1_conservation_threats_baseline_year');

  const customExplainerText = useFact<string>('r1_conservation_degradation_drivers_historic_explainer');

  const fallbackExplainerText = useGetExplainerFallbackText({
    trend: {
      label: trendLabel,
      value: changePctFromBaseline,
    },
    indicator: 'degradationDrivers',
  })?.trendText;

  return (
    <Stack spacing={10} className='px-6' data-testid='degradation-drivers-historic-stats-tile'>
      <Stack spacing={6}>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.habitatIntactness.degradationDriversHistoric.statsHeading')}
          popover={{
            title: t(
              'shared.ncaDetail.habitatIntactness.degradationDriversHistoric.explainers.degradationDrivers.title',
            ),
            body: t('shared.ncaDetail.habitatIntactness.degradationDriversHistoric.explainers.degradationDrivers.body'),
          }}
          supplementaryInfo={latestAnalysisDatestamp}
        />
        <Stack spacing={16} className='flex-wrap md:flex-row'>
          <FactCapsule
            label={t('shared.ncaDetail.habitatIntactness.degradationDriversHistoric.baselineValue')}
            fact={projectBaseline}
            capsuleIconProps={{ icon: <RiCompass4Line /> }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.habitatIntactness.degradationDriversHistoric.explainers.baselineValue.title'),
              body: t('shared.ncaDetail.habitatIntactness.degradationDriversHistoric.explainers.baselineValue.body', {
                year: baselineYear.value,
              }),
              popoverTriggerProps: {
                'data-testid': 'baseline-value-popover',
              },
            }}
          />
          <FactCapsule
            label={t('shared.ncaDetail.habitatIntactness.degradationDriversHistoric.latestValue', {
              year: currentYear.value,
            })}
            fact={projectLatestValue}
            capsuleIconProps={{ icon: <RiCompass4Line /> }}
            trend={<DegradationDriversTrend trend={changePctFromBaseline} />}
            infoPopoverProps={{
              title: t('shared.ncaDetail.habitatIntactness.degradationDriversHistoric.explainers.latestValue.title', {
                year: currentYear.value,
              }),
              body: t('shared.ncaDetail.habitatIntactness.degradationDriversHistoric.explainers.latestValue.body', {
                year: currentYear.value,
              }),
              popoverTriggerProps: {
                'data-testid': 'latest-value-popover',
              },
            }}
          />
        </Stack>
      </Stack>
      <ConservationExplainerWell
        trendLabel={trendLabel}
        title={t('global.whatDoesThisMean')}
        variant='negative'
        body={
          customExplainerText?.value ? (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(customExplainerText.value),
              }}
            />
          ) : (
            <span className='whitespace-pre-wrap'>{fallbackExplainerText}</span>
          )
        }
      />
    </Stack>
  );
};

const DegradationDriversTrend = ({ trend }: { trend: number }) => {
  if (isNullish(trend)) {
    return null;
  }

  const trendDisplay = `${getDisplayNumber(trend, undefined, {
    signDisplay: 'always',
  })}%`;

  return <CapsuleTrend variant={trend <= 0 ? 'positive' : 'negative'}>{trendDisplay}</CapsuleTrend>;
};
